/**
 * Iframe-to-parent communication utilities.
 * Used when the Classified Order Entry app is embedded as an iframe.
 */

const isEmbedded = () => window.parent !== window

// Fullscreen state for modal-pending + fallback timeout
let fullscreenConfirmed = false
let fallbackTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Initialize listening for messages from the parent page.
 *
 * Handles two message types:
 *   FB_IFRAME_SCROLL_OFFSET — parent sends scroll position & viewport height.
 *     Used as CSS variables for the fallback absolute-positioning approach.
 *   FB_IFRAME_FULLSCREEN_ACTIVE — parent confirms the iframe has been made
 *     fullscreen (100vw x 100vh). Enables the fullscreen CSS mode where
 *     position:fixed works correctly inside the iframe.
 */
export function initParentScrollTracking() {
  if (!isEmbedded()) return

  // Set initial fallback values so modals work even before the parent sends scroll info.
  document.documentElement.style.setProperty('--parent-scroll-top', '0px')
  document.documentElement.style.setProperty('--parent-viewport-height', '800px')

  window.addEventListener('message', (event) => {
    if (event.data?.type === 'FB_IFRAME_SCROLL_OFFSET') {
      const scrollTop = Number(event.data.scrollTop) || 0
      const viewportHeight = Number(event.data.viewportHeight) || window.innerHeight
      document.documentElement.style.setProperty('--parent-scroll-top', scrollTop + 'px')
      document.documentElement.style.setProperty('--parent-viewport-height', viewportHeight + 'px')
      document.documentElement.classList.add('has-parent-scroll')
    }

    // Parent confirmed fullscreen is active — enable fullscreen CSS mode.
    // This switches modals from position:absolute (fallback) to position:fixed
    // which works correctly when the iframe fills the viewport.
    if (event.data?.type === 'FB_IFRAME_FULLSCREEN_ACTIVE') {
      fullscreenConfirmed = true
      if (fallbackTimer) {
        clearTimeout(fallbackTimer)
        fallbackTimer = null
      }
      // Parent confirmed the iframe is now fullscreen (100vw x 100vh).
      // Enable fullscreen CSS mode and reveal the hidden modal.
      document.documentElement.classList.add('iframe-fullscreen')
      document.documentElement.classList.remove('modal-pending')
    }
  })

  // Request scroll info from the parent immediately.
  window.parent.postMessage({ type: 'FB_IFRAME_READY' }, '*')
}

/**
 * Notify the parent that a modal/dialog has been opened.
 * New embed codes will make the iframe fullscreen and send FB_IFRAME_FULLSCREEN_ACTIVE back.
 * Old embed codes will scroll the iframe into view (fallback).
 */
export function notifyModalOpened() {
  if (!isEmbedded()) return

  // Hide the modal during the postMessage round-trip to the parent.
  // The parent will make the iframe fullscreen and confirm back; only then
  // do we reveal the modal (with position:fixed working correctly).
  document.documentElement.classList.add('modal-pending')
  fullscreenConfirmed = false

  window.parent.postMessage({ type: 'FB_IFRAME_MODAL_OPENED' }, '*')

  // Fallback: if the parent never confirms fullscreen (old embed code),
  // just reveal the modal with the fallback absolute positioning.
  fallbackTimer = setTimeout(() => {
    if (!fullscreenConfirmed) {
      document.documentElement.classList.remove('modal-pending')
    }
    fallbackTimer = null
  }, 300)
}

/**
 * Notify the parent that a modal/dialog has been closed.
 * Removes fullscreen class (if set by parent confirmation) and tells parent to restore.
 */
export function notifyModalClosed() {
  if (!isEmbedded()) return
  fullscreenConfirmed = false
  if (fallbackTimer) {
    clearTimeout(fallbackTimer)
    fallbackTimer = null
  }
  document.documentElement.classList.remove('iframe-fullscreen')
  document.documentElement.classList.remove('modal-pending')
  window.parent.postMessage({ type: 'FB_IFRAME_MODAL_CLOSED' }, '*')
}
